import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { EditMultiTableReservationFloorPlan } from './EditMultiTableReservationFloorPlan';
import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import { ServiceReservation } from './apiHelpers';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FloorPlanTableData } from '@shared/types/floorPlans';
import { intersection } from 'lodash-es';
import {
  MultiTableBookingForm,
  type MultiTableReservationFormData,
} from '../concierge/MultiTableBookingForm';
import styles from './EditMultiTableReservationDrawer.scss';
import { useUpdateLargePartyReservationMutation } from './hooks/useUpdateLargePartyReservationMutation';
import { useServiceSearchParams } from './hooks/useServiceSearchParams';
import {
  checkMultiTableReservationForOverlaps,
  type OverlappingReservation,
} from '../concierge/apiHelpers';
import { GodModeOverbookingWarningModal } from 'restaurantAdmin/reservations/shared/GodModeOverbookingWarningModal';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';

interface EditMultiTableReservationDrawerProps {
  reservation: ServiceReservation;
}

export const EditMultiTableReservationDrawer = ({
  reservation,
}: EditMultiTableReservationDrawerProps) => {
  const {
    floorPlans,
    isLoading: isFloorPlanLoading,
    isReadyForDisplay,
    setSelectedFloorPlanId,
    selectedFloorPlan,
  } = useAdminFloorPlans();

  const { exitReservationEditMode } = useServiceSearchParams();
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  const { mutate: updateLargePartyReservation } =
    useUpdateLargePartyReservationMutation({ reservationId: reservation.id });

  const {
    isOpen: isUnassignmentWarningModalOpen,
    open: openUnassignmentWarningModal,
    close: closeUnassignmentWarningModal,
    data: modalData,
  } = useIsOpenWithData<{
    formData: MultiTableReservationFormData;
    overlappingReservations: OverlappingReservation[];
  }>();

  const [selectedFloorPlanTables, setSelectedFloorPlanTables] = useState<
    FloorPlanTableData[]
  >([]);

  const highlightedListingIds = reservation.tableAssignments.map(
    (t) => t.listingId,
  );

  useEffect(() => {
    const floorPlanToSelect = floorPlans.find((floorPlan) => {
      return floorPlan.floorPlanTables.some((floorPlanTable) => {
        return floorPlanTable.listings.some((listing) => {
          return highlightedListingIds.includes(listing.id);
        });
      });
    });
    if (!floorPlanToSelect) return;

    setSelectedFloorPlanId(floorPlanToSelect.id);

    setSelectedFloorPlanTables(
      floorPlans.flatMap((floorPlan) =>
        floorPlan.floorPlanTables.filter(
          (t) =>
            intersection(
              t.listings.map((l) => l.id),
              highlightedListingIds,
            ).length > 0,
        ),
      ),
    );
  }, [isFloorPlanLoading]);

  if (!isReadyForDisplay) return null;

  const handleTableOnClick = (floorPlanTable: FloorPlanTableData) => {
    if (selectedFloorPlanTables.some((t) => t.id === floorPlanTable.id)) {
      const newSelectedFloorPlanTables = selectedFloorPlanTables.filter(
        (t) => t.id !== floorPlanTable.id,
      );
      setSelectedFloorPlanTables(newSelectedFloorPlanTables);
    } else {
      const newSelectedFloorPlanTables = [
        ...selectedFloorPlanTables,
        floorPlanTable,
      ];
      setSelectedFloorPlanTables(newSelectedFloorPlanTables);
    }
  };

  const submitChanges = (formData: MultiTableReservationFormData) => {
    updateLargePartyReservation(
      {
        floorPlanTableIds: selectedFloorPlanTables.map((t) => t.id),
        guestCount: Number(formData.guestCount),
        duration: Number(formData.duration),
        date: formData.date,
        time: formData.time,
      },
      {
        onSuccess: () => {
          exitReservationEditMode();
          // Refresh occupants data when drawer closes
          queryClient.invalidateQueries({
            queryKey: ['restaurant', restaurantId, 'occupants'],
          });
        },
      },
    );
  };

  const handleSubmitClick = async (formData: MultiTableReservationFormData) => {
    const { wouldOverlap, overlappingReservations } =
      await checkMultiTableReservationForOverlaps(restaurantId, {
        floorPlanTableIds: selectedFloorPlanTables.map((t) => t.id),
        duration: Number(formData.duration),
        date: formData.date,
        time: formData.time,
        excludeReservationId: reservation.id,
      });

    if (wouldOverlap) {
      openUnassignmentWarningModal({ formData, overlappingReservations });
    } else {
      submitChanges(formData);
    }
  };

  return (
    <div className={styles.drawerContent}>
      <FloorPlanSelector
        floorPlans={floorPlans}
        selectedFloorPlan={selectedFloorPlan}
        onChange={(selectedFloorPlanId) => {
          setSelectedFloorPlanId(selectedFloorPlanId);
        }}
        fullWidth={true}
      />
      <EditMultiTableReservationFloorPlan
        tables={selectedFloorPlan.floorPlanTables}
        backgroundSrc={selectedFloorPlan.backgroundSrc}
        selectedFloorPlanTables={selectedFloorPlanTables}
        handleTableOnClick={handleTableOnClick}
      />
      <MultiTableBookingForm
        tableLabelPosition="top"
        submitButtonText="Save Edits"
        selectedFloorPlanTables={selectedFloorPlanTables}
        handleSubmitClick={handleSubmitClick}
        reservation={reservation}
        onCancel={exitReservationEditMode}
      />
      {modalData && (
        <GodModeOverbookingWarningModal
          isOpen={isUnassignmentWarningModalOpen}
          onClose={closeUnassignmentWarningModal}
          onConfirm={submitChanges}
          formData={modalData.formData}
          overlappingReservations={modalData.overlappingReservations}
        />
      )}
    </div>
  );
};
